import { type FC, useState } from "react";
import { Box, CircularProgress, Skeleton } from "@mui/material";
import { useQuery } from "@apollo/client";
import {
  useQueryWithSSRErrors,
  usePlatform,
  useViewerHost,
} from "@/shared/hooks";
import { CollectionPlaylistQuery, ContentQuery } from "@/entities/contents";
import {
  PlatformFeatureType,
  type CollectionPlaylistQueryType,
  type ContentQueryType,
} from "@/types/graphql/site.generated";
import { ErrorScreen } from "@/shared/components";
import { ContentPlayer } from "@/shared/components/ContentPlayer";
import { FixWithAssistantButton } from "@/features/assistant";
import { useContentPurchase } from "@/widgets/ContentPurchase";
import { CloseRouteDialogButton } from "@/shared/components/RouteDialog";
import { PromoButtonWidget } from "@/shared/components/PromoButtonWidget";
import { ContentChat } from "@/widgets/ContentChat";
import { ContentBody } from "./ContentBody";
import { ContentLayout } from "./ContentLayout";
import { ContentPlaylist } from "./ContentPlaylist";
import { FeaturedVideos } from "./FeaturedVideos";
import { NextContentButton } from "./NextContentButton";
import { MetaHead } from "@/shared/components/MetaHead";
import {
  embedPathOf,
  metaImageOf,
} from "@/shared/components/MetaHead/metaTags";
import {
  StructuredData,
  buildBreadcrumbs,
  buildVideoObject,
} from "@/shared/components/StructuredData";
import { PollsOverlay } from "@/features/polls";
import { platformHasFeature } from "@/shared/utils/platformFeatures";

// Экран видео, общий для двух способов показа (как в референсе, где один
// VideoScreenContent живёт и на полной странице, и внутри RouteDialog):
//  - страница /video/[id] (прямой заход, SSR) передаёт id из router.query;
//  - _app рендерит его в route-диалоге поверх текущей страницы, когда URL
//    сменился мимо Next-роутера (см. RouteDialog/Link, openRouteDialog).
// Компоновка — референсный Video.js: основная колонка (плеер + ContentBody)
// на всю ширину, справа фиксированный сайдбар «Похожие видео» (desktop),
// на мобилке тот же список ниже. Сайдбар и автопереход к следующему видео
// включаются флагом платформы isFeaturedVideosEnabled.
// collectionId — плейлист-контекст: видео открыто из полки/страницы одной
// коллекции (?collectionId= в URL) — сайдбар показывает плейлист коллекции
// вместо «Похожих», автопереход идёт по её порядку (референсный Video.js).
export const VideoScreen: FC<{ id?: string; collectionId?: string }> = ({
  id,
  collectionId,
}) => {
  const { platform } = usePlatform();
  const host = useViewerHost();
  // Мобильный слот под плеером (спека live-reactions-and-polls): туда
  // уезжают панель реакций и карточка опроса на узких экранах, оверлеи
  // получают сам элемент через PlayerOverlaySlot.
  const [mobileSlot, setMobileSlot] = useState<HTMLDivElement | null>(null);
  const { data, previousData, loading, error, refetch } =
    useQueryWithSSRErrors<ContentQueryType>(ContentQuery, {
      variables: { id },
      skip: !id,
    });
  // Коллекция плейлиста для хлебных крошек JSON-LD: тот же запрос, что у
  // ContentPlaylist в сайдбаре, Apollo склеивает их в один.
  const { data: playlistData } = useQuery<CollectionPlaylistQueryType>(
    CollectionPlaylistQuery,
    { variables: { id: collectionId }, skip: !collectionId },
  );
  // Покупка с экрана оплаты плеера без ухода на страницу продукта; после
  // успешной оплаты контент перезапрашивается и плеер получает доступ.
  const purchase = useContentPurchase(() => void refetch());
  // Как в референсном Video.js: во время перехода диалог→диалог (в т.ч.
  // автоперехода к следующему видео) показываем предыдущий контент, пока
  // грузится новый — плеер не размонтируется и фулскрин не слетает.
  const content = data?.content ?? previousData?.content;

  const showFeatured = platform?.isFeaturedVideosEnabled !== false;

  // Опросы: только фичефлаг админа PLAYER_POLLS, партнёрского тумблера нет.
  const pollsEnabled = platformHasFeature(
    platform,
    PlatformFeatureType.PlayerPollsType,
  );

  // Первое открытие (SSR-данных нет, показать нечего): референс держит ту
  // же компоновку — сайдбар «Похожие видео» живёт сразу (ему нужен только
  // contentId из URL), а в главной колонке — тёмная зона плеера со
  // спиннером и кнопкой закрытия + скелетон тела. Так открытие диалога не
  // перескакивает с центрованного плеера на layout с сайдбаром. При
  // переходах диалог→диалог сюда не попадаем — есть previousData.
  if ((loading && !content) || !id) {
    let loadingSidebar = null;
    if (collectionId) {
      loadingSidebar = (
        <ContentPlaylist collectionId={collectionId} variant="sidebar" />
      );
    } else if (showFeatured && id) {
      loadingSidebar = <FeaturedVideos contentId={id} variant="sidebar" />;
    }

    return (
      <ContentLayout sidebar={loadingSidebar}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
          <CloseRouteDialogButton />
        </Box>
        <Box sx={{ pt: { xs: 1.5, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
          <Skeleton variant="text" sx={{ fontSize: "2rem", maxWidth: 480 }} />
          <Skeleton
            variant="rounded"
            sx={{ mt: 2, borderRadius: 50, height: 42, maxWidth: 360 }}
          />
        </Box>
      </ContentLayout>
    );
  }
  if (error) return <ErrorScreen error={error} />;
  if (!content) return <ErrorScreen code={404} />;

  const ogImage = metaImageOf(content.preview);
  const thumbnailUrl =
    ogImage?.url ?? (platform?.logo?.url ? String(platform.logo.url) : null);
  const playlistCollection =
    collectionId && playlistData?.content?.id === collectionId
      ? playlistData.content
      : null;

  let sidebar = null;
  if (collectionId) {
    sidebar = <ContentPlaylist collectionId={collectionId} variant="sidebar" />;
  } else if (showFeatured) {
    sidebar = <FeaturedVideos contentId={content.id} variant="sidebar" />;
  }

  return (
    <>
      <MetaHead
        title={content.title}
        description={content.description ?? content.title}
        image={ogImage}
        path={content.path}
        url={content.url ? String(content.url) : null}
        type="video.other"
        tags={content.tags}
        videoDuration={content.duration}
        videoReleaseDate={content.publishAt}
        embedPath={embedPathOf(content.id)}
      />
      {platform && (
        <StructuredData
          nodes={[
            buildVideoObject(host, platform, content, thumbnailUrl),
            buildBreadcrumbs(host, platform, [
              playlistCollection
                ? {
                    name: playlistCollection.title,
                    path: playlistCollection.path,
                  }
                : null,
              { name: content.title, path: content.path },
            ]),
          ]}
        />
      )}
      <ContentLayout sidebar={sidebar}>
        {/* Без key: смена контента (автопереход, клик по похожему) должна
            переиспользовать смонтированный плеер — remount выбивал бы
            фулскрин; сброс view-состояния ContentPlayer делает сам. */}
        <ContentPlayer
          content={content}
          source="SITE"
          autoPlay
          onProductClick={purchase.openProducts}
          renderAssistantAction={({ errorCode, contentId, playerError }) => (
            <FixWithAssistantButton
              errorCode={errorCode}
              contentId={contentId}
              playerError={playerError}
            />
          )}
          overlay={({ controlsVisible, currentTime, isCasting }) => (
            <>
              <CloseRouteDialogButton />
              <PromoButtonWidget
                contentId={content.id}
                widget={content.widget}
                visible={controlsVisible}
              />
              {pollsEnabled && !content.siteAccess.error && (
                <PollsOverlay
                  contentId={content.id}
                  hostType="VIDEO"
                  initialPolls={content.polls}
                  controlsVisible={controlsVisible}
                  currentTime={currentTime}
                  isCasting={isCasting}
                  mobileSlot={mobileSlot}
                />
              )}
            </>
          )}
          timecodes={content.contentTimecodes?.timecodes}
          nextButton={
            showFeatured || collectionId
              ? (videoState, controlsVisible) => (
                  <NextContentButton
                    contentId={content.id}
                    collectionId={collectionId}
                    currentTime={videoState.currentTime}
                    duration={videoState.duration}
                    ended={videoState.ended}
                    visible={controlsVisible}
                  />
                )
              : undefined
          }
        />
        <Box
          ref={setMobileSlot}
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            gap: 1,
            px: 1.5,
            pt: 1,
            "&:empty": { display: "none" },
          }}
        />
        <ContentBody content={content} />
        {content.isChatEnabled && content.chatRoom && (
          <Box sx={{ pt: 1 }}>
            <ContentChat
              chatRoom={{
                id: String(content.chatRoom.id),
                viewerIsModerator: content.chatRoom.viewerIsModerator,
                viewerBan: content.chatRoom.viewerBan ?? null,
              }}
              mode="video"
              variant="page"
            />
          </Box>
        )}
        {collectionId ? (
          <Box sx={{ display: { xs: "block", md: "none" }, pt: 1 }}>
            <ContentPlaylist collectionId={collectionId} variant="mobile" />
          </Box>
        ) : (
          showFeatured && (
            <Box sx={{ display: { xs: "block", md: "none" }, pt: 1 }}>
              <FeaturedVideos contentId={content.id} variant="mobile" />
            </Box>
          )
        )}
      </ContentLayout>
      {purchase.dialogs}
    </>
  );
};
